/**
 * GeoPage.jsx
 * Geographic sales performance — revenue and units by country/region,
 * with drilldown into provinces and top products per market.
 *
 * Two views:
 *   A (default): Countries ranked by revenue with expandable province rows
 *   B: Product-by-country — select a product and see which countries buy it most
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  EmptyState,
  Text,
  BlockStack,
  Badge,
  Pagination,
  Select,
  InlineStack,
  TextField,
  Button,
} from '@shopify/polaris';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';

import { useReport } from '../hooks/useReport';
import { api } from '../api/api';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  periodLabel,
  countryCodeToFlag,
} from '../utils/formatters';
import { exportToCsv } from '../utils/exportCsv';

const GEO_CSV_COLUMNS = [
  { key: 'country', label: 'Country' },
  { key: 'countryCode', label: 'Country Code' },
  { key: 'orderCount', label: 'Orders' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'unitsSold', label: 'Units Sold' },
  { key: 'topProducts', label: 'Top Products' },
];

const ROWS_PER_PAGE = 20;
const CHART_HEIGHT = 300;
const CHART_FILL = '#008060';
const TOP_PRODUCTS_COUNT = 3;
const CHART_COUNTRY_COUNT = 10;

const SORT_OPTIONS = [
  { label: 'Revenue (high → low)', value: 'revenue:desc' },
  { label: 'Revenue (low → high)', value: 'revenue:asc' },
  { label: 'Orders (high → low)', value: 'orderCount:desc' },
  { label: 'Units sold (high → low)', value: 'unitsSold:desc' },
  { label: 'Country A → Z', value: 'country:asc' },
];

const VIEW_OPTIONS = [
  { label: 'Countries by revenue', value: 'country' },
  { label: 'Product by country', value: 'product' },
];

/**
 * Aggregate province-level geo rows into country-level summaries.
 *
 * @param {Array} geoRows - raw rows from API (one per country+province)
 * @returns {Array} country-level rows with provinces nested
 */
function aggregateByCountry(geoRows) {
  const countryMap = new Map();

  for (const row of geoRows) {
    const key = row.country;
    const existing = countryMap.get(key);

    if (!existing) {
      countryMap.set(key, {
        country: row.country,
        countryCode: row.countryCode,
        orderCount: row.orderCount,
        revenue: row.revenue,
        unitsSold: row.unitsSold,
        products: new Map(),
        provinces: row.province ? [row] : [],
      });
    } else {
      existing.orderCount += row.orderCount;
      existing.revenue += row.revenue;
      existing.unitsSold += row.unitsSold;
      if (row.province) existing.provinces.push(row);
    }

    // Merge products across provinces for the country total
    const entry = countryMap.get(key);
    for (const p of row.products || []) {
      const ep = entry.products.get(p.variantId);
      if (!ep) {
        entry.products.set(p.variantId, { ...p });
      } else {
        ep.unitsSold += p.unitsSold;
        ep.revenue += p.revenue;
      }
    }
  }

  return [...countryMap.values()].map((c) => ({
    ...c,
    products: [...c.products.values()].sort((a, b) => b.revenue - a.revenue),
  }));
}

/**
 * Sort country rows by the selected field.
 *
 * @param {Array} rows
 * @param {string} sortBy
 * @param {string} sortDir
 * @returns {Array}
 */
function sortCountries(rows, sortBy, sortDir) {
  return [...rows].sort((a, b) => {
    const aVal = a[sortBy] ?? '';
    const bVal = b[sortBy] ?? '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });
}

/**
 * GeoPage — geographic sales performance.
 *
 * @param {{ period: string, filterBar: JSX.Element }} props
 */
export function GeoPage({ period, filterBar }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('revenue:desc');
  const [view, setView] = useState('country');
  const [expandedCountries, setExpandedCountries] = useState(new Set());
  const [selectedProduct, setSelectedProduct] = useState('');
  const [search, setSearch] = useState('');

  const [sortBy, sortDir] = sort.split(':');

  // Fetch all geo data (high limit to get everything for client-side aggregation)
  const { data, loading, error } = useReport(api.getGeo, {
    period,
    limit: 500,
    sortBy: 'revenue',
    sortDir: 'desc',
  });

  const rawGeoRows = data?.data || [];

  // Aggregate province rows into countries
  const countries = useMemo(() => aggregateByCountry(rawGeoRows), [rawGeoRows]);

  // Total revenue for % calculations
  const totalRevenue = useMemo(
    () => countries.reduce((sum, c) => sum + c.revenue, 0),
    [countries],
  );

  // Sorted + paginated countries for View A
  const sortedCountries = useMemo(
    () => sortCountries(countries, sortBy, sortDir),
    [countries, sortBy, sortDir],
  );

  const totalPages = Math.ceil(sortedCountries.length / ROWS_PER_PAGE);
  const pageStart = (page - 1) * ROWS_PER_PAGE;
  const pagedCountries = sortedCountries.slice(pageStart, pageStart + ROWS_PER_PAGE);

  // Build unique product list for View B selector
  const productOptions = useMemo(() => {
    const productMap = new Map();
    for (const c of countries) {
      for (const p of c.products) {
        if (!productMap.has(p.variantId)) {
          productMap.set(p.variantId, p.productTitle);
        }
      }
    }
    return [
      { label: 'Select a product…', value: '' },
      ...[...productMap.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, title]) => ({ label: title, value: id })),
    ];
  }, [countries]);

  // Product-by-country rows for View B
  const productByCountryRows = useMemo(() => {
    if (!selectedProduct) return [];
    return countries
      .map((c) => {
        const match = c.products.find((p) => p.variantId === selectedProduct);
        if (!match) return null;
        return { ...c, productRevenue: match.revenue, productUnits: match.unitsSold };
      })
      .filter(Boolean)
      .sort((a, b) => b.productRevenue - a.productRevenue);
  }, [countries, selectedProduct]);

  // Chart data — top 10 countries by revenue
  const chartData = useMemo(
    () =>
      sortCountries(countries, 'revenue', 'desc')
        .slice(0, CHART_COUNTRY_COUNT)
        .map((c) => ({
          name: c.countryCode
            ? `${countryCodeToFlag(c.countryCode)} ${c.countryCode}`
            : c.country.slice(0, 10),
          revenue: c.revenue,
        })),
    [countries],
  );

  const handleExportGeo = useCallback(() => {
    const csvRows = sortedCountries.map((c) => ({
      country: c.country,
      countryCode: c.countryCode,
      orderCount: c.orderCount,
      revenue: c.revenue,
      unitsSold: c.unitsSold,
      topProducts: c.products.slice(0, TOP_PRODUCTS_COUNT).map((p) => p.productTitle).join('; '),
    }));
    const filename = `geo-performance-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    exportToCsv(filename, csvRows, GEO_CSV_COLUMNS);
  }, [sortedCountries, period]);

  const toggleCountry = (country) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      next.has(country) ? next.delete(country) : next.add(country);
      return next;
    });
  };

  // Build DataTable rows for View A — country rows with optional province sub-rows
  const countryTableRows = useMemo(() => {
    const rows = [];
    const filtered = search
      ? pagedCountries.filter((c) =>
          c.country.toLowerCase().includes(search.toLowerCase()))
      : pagedCountries;

    for (const c of filtered) {
      const flag = countryCodeToFlag(c.countryCode);
      const pct = totalRevenue > 0 ? formatPercent((c.revenue / totalRevenue) * 100) : '0.0%';
      const top3 = c.products
        .slice(0, TOP_PRODUCTS_COUNT)
        .map((p) => p.productTitle)
        .join(', ');
      const isExpanded = expandedCountries.has(c.country);

      rows.push([
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Button
            size="slim"
            variant="plain"
            onClick={() => toggleCountry(c.country)}
            disclosure={isExpanded ? 'up' : 'down'}
            accessibilityLabel={`Expand ${c.country}`}
          >
            {flag ? `${flag} ${c.country}` : c.country}
          </Button>
        </InlineStack>,
        formatNumber(c.orderCount),
        <Text fontWeight="semibold" as="span">{formatCurrency(c.revenue)}</Text>,
        formatNumber(c.unitsSold),
        <Badge>{pct}</Badge>,
        <Text variant="bodySm" tone="subdued" as="span">{top3 || '—'}</Text>,
      ]);

      // Province sub-rows when expanded
      if (isExpanded && c.provinces.length > 0) {
        for (const prov of c.provinces.sort((a, b) => b.revenue - a.revenue)) {
          const provPct = totalRevenue > 0
            ? formatPercent((prov.revenue / totalRevenue) * 100)
            : '0.0%';
          const provTop3 = (prov.products || [])
            .slice(0, TOP_PRODUCTS_COUNT)
            .map((p) => p.productTitle)
            .join(', ');

          rows.push([
            <Text variant="bodySm" tone="subdued" as="span">
              {'    '}› {prov.province}
            </Text>,
            formatNumber(prov.orderCount),
            <Text tone="subdued" as="span">{formatCurrency(prov.revenue)}</Text>,
            formatNumber(prov.unitsSold),
            <Text variant="bodySm" tone="subdued" as="span">{provPct}</Text>,
            <Text variant="bodySm" tone="subdued" as="span">{provTop3 || '—'}</Text>,
          ]);
        }
      }
    }
    return rows;
  }, [pagedCountries, expandedCountries, totalRevenue, search]);

  // Build DataTable rows for View B — product by country
  const productTableRows = useMemo(() => {
    return productByCountryRows.map((c) => {
      const flag = countryCodeToFlag(c.countryCode);
      return [
        flag ? `${flag} ${c.country}` : c.country,
        formatNumber(c.orderCount),
        <Text fontWeight="semibold" as="span">{formatCurrency(c.productRevenue)}</Text>,
        formatNumber(c.productUnits),
      ];
    });
  }, [productByCountryRows]);

  const hasData = !loading && !error;
  const isEmpty = hasData && countries.length === 0;

  return (
    <BlockStack gap="400">
      <Card>{filterBar}</Card>

      {/* Bar chart — top 10 countries */}
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h2">
            Revenue by Country — {periodLabel(period)}
          </Text>
          {loading ? (
            <SkeletonBodyText lines={8} />
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E5E7" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#637381' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: '#637381' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: 8, border: '1px solid #E4E5E7' }}
                />
                <Bar dataKey="revenue" fill={CHART_FILL} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : null}
        </BlockStack>
      </Card>

      {/* View toggle + filters */}
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center" wrap>
            <Text variant="headingMd" as="h2">
              {view === 'country' ? 'Country / Region Breakdown' : 'Product by Country'}
            </Text>
            <InlineStack gap="300" blockAlign="center">
              {hasData && (
                <Text variant="bodySm" tone="subdued" as="span">
                  {formatNumber(countries.length)} countries
                </Text>
              )}
              <Button
                size="slim"
                onClick={handleExportGeo}
                disabled={loading || countries.length === 0}
              >
                Export CSV
              </Button>
            </InlineStack>
          </InlineStack>

          <InlineStack gap="300" wrap>
            <div style={{ minWidth: 200 }}>
              <Select
                label="" labelHidden
                options={VIEW_OPTIONS}
                value={view}
                onChange={(v) => { setView(v); setPage(1); }}
              />
            </div>

            {view === 'country' && (
              <>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <TextField
                    label="" labelHidden
                    placeholder="Search country…"
                    value={search}
                    onChange={(v) => { setSearch(v); setPage(1); }}
                    clearButton
                    onClearButtonClick={() => { setSearch(''); setPage(1); }}
                    autoComplete="off"
                  />
                </div>
                <div style={{ minWidth: 220 }}>
                  <Select
                    label="" labelHidden
                    options={SORT_OPTIONS}
                    value={sort}
                    onChange={(v) => { setSort(v); setPage(1); }}
                  />
                </div>
              </>
            )}

            {view === 'product' && (
              <div style={{ flex: 1, minWidth: 260 }}>
                <Select
                  label="" labelHidden
                  options={productOptions}
                  value={selectedProduct}
                  onChange={setSelectedProduct}
                />
              </div>
            )}
          </InlineStack>

          {error && (
            <Banner title="Error loading geo data" tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {loading && <SkeletonBodyText lines={12} />}

          {isEmpty && (
            <EmptyState
              heading="No geographic data available"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                No orders with shipping addresses were found for this period.
                Try selecting a longer time range.
              </p>
            </EmptyState>
          )}

          {/* View A: Country table */}
          {hasData && view === 'country' && countryTableRows.length > 0 && (
            <>
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                headings={[
                  'Country', 'Orders', 'Revenue', 'Units Sold',
                  '% of Revenue', 'Top Products',
                ]}
                rows={countryTableRows}
                footerContent={`Page ${page} of ${totalPages}`}
              />
              <Pagination
                hasPrevious={page > 1}
                hasNext={page < totalPages}
                onPrevious={() => setPage((p) => p - 1)}
                onNext={() => setPage((p) => p + 1)}
                label={`Page ${page} of ${totalPages}`}
              />
            </>
          )}

          {/* View B: Product by country */}
          {hasData && view === 'product' && (
            <>
              {!selectedProduct ? (
                <Text variant="bodyMd" tone="subdued" as="p">
                  Select a product above to see which countries buy it most.
                </Text>
              ) : productTableRows.length === 0 ? (
                <Banner tone="info">
                  <p>No orders found for this product in the selected period.</p>
                </Banner>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                  headings={['Country', 'Orders', 'Product Revenue', 'Product Units']}
                  rows={productTableRows}
                />
              )}
            </>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
